import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { useDashboards } from "../context/DashboardsContext";
import * as api from "../api/client";
import WidgetCard from "../components/WidgetCard";
import KpiWidget, { cardKey, fmtPeriod, mergeKpiWidgets } from "../components/KpiWidget";
import { mdToHtml } from "../utils/markdown";
import LineageModal from "../components/LineageModal";
import AddChatsModal from "../components/AddChatsModal";
import ShareDashboardModal from "../components/ShareDashboardModal";
import WidgetBuilderModal from "../components/WidgetBuilderModal";
import { useAuth } from "../context/AuthContext";
import { dashboardChartImages } from "../utils/chartImage";
//import { mdToHtml } from "../utils/markdown";

// Reasons the owner asked not to be shown to the user - the widget still gets its stale badge (so it
// is visibly not current), but these two explanations read as internal plumbing rather than something
// a viewer needs to act on. Any OTHER reason (e.g. "it has no saved query to re-run") still shows.
const HIDDEN_REFRESH_REASONS = new Set([
  "built from a raw SQL query, which has no single date column to filter on",
  "no rows fall in that date range",
]);

/** The dashboard's own title, editable in place. Mirrors ChatPage's ChatTitle. */
function DashboardTitle({ title, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = () => {
    setDraft(title || "");
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (!next || next === title) return;
    onRename(next);
  };

  if (editing) {
    return (
      <input
        className="chat-title-input"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }
  if (!onRename) return <h1>{title}</h1>;
  return (
    <h1 className="chat-title-editable" title="Click to rename" onClick={startEdit}>
      {title}
    </h1>
  );
}

/** One widget card's header label, editable in place — independent of `sourceTitle` (which chat it
    came from) and `spec.title` (what the model called the chart), neither of which the owner can
    otherwise change. Blank clears the override and falls back to sourceTitle again. */
function WidgetTitle({ widget, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const label = widget.title || widget.sourceTitle;

  const startEdit = () => {
    setDraft(widget.title || "");
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next === (widget.title || "")) return;
    onRename(next);
  };

  if (editing) {
    return (
      <input
        className="who who-input"
        autoFocus
        value={draft}
        placeholder={widget.sourceTitle}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }
  if (!onRename) return <span className="who">{label}</span>;
  return (
    <span className="who who-editable" title="Click to rename" onClick={startEdit}>
      {label}
    </span>
  );
}

/** One KPI tile. `hint` is the small line under the number. */
function Kpi({ label, value, hint, icon }) {
  return (
    <div className="kpi">
      <div className="kpi-top">
        <span className="kpi-label">{label}</span>
        <span className="kpi-icon">{icon}</span>
      </div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}

export default function DashboardDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { removeDashboard, refresh: refreshDashboardList } = useDashboards();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [showLineage, setShowLineage] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshInfo, setRefreshInfo] = useState(null);
  const [expanded, setExpanded] = useState(null);   // widget shown full-size, or null
  const [removingId, setRemovingId] = useState(null);
  const [adding, setAdding] = useState(false);      // the "add from another chat" picker is open
  const [sharing, setSharing] = useState(false);    // the share-with-roles dialog is open
  // The widget builder: null (closed), {} (new widget) or {widget} (editing a custom widget).
  const [builder, setBuilder] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // A dashboard just created empty (DashboardsPage "New dashboard") opens on the builder.
  useEffect(() => {
    if (searchParams.get("build") === "1") {
      setBuilder({});
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);  // false, or the format being prepared
  // Kept apart from `error`, which replaces the whole page: a failed download must not hide the dashboard.
  const [downloadError, setDownloadError] = useState(null);

  // Drag-to-rearrange. `armedId` is which card has HTML5 dragging switched on: it is set on
  // mousedown over that card's grip and cleared when the drag ends, so the cards are not draggable
  // the rest of the time. Marking the whole card draggable permanently instead would fight every
  // other interaction in it — you could not drag-select the narration text, and in some browsers the
  // chart-type <select> and the rename input stop behaving normally inside a draggable ancestor.
  const [armedId, setArmedId] = useState(null);
  const [dragId, setDragId] = useState(null);       // card currently being dragged
  const [overId, setOverId] = useState(null);       // card it is hovering over, for the drop line
  const [savingOrder, setSavingOrder] = useState(false);
  const [showKpiNarration, setShowKpiNarration] = useState(false);
  // Failures of the Key metrics actions. Kept apart from `error`, which replaces the whole page — a
  // card that couldn't be moved or removed shouldn't take the dashboard away with it.
  const [kpiError, setKpiError] = useState(null);

  // One card out of the Key metrics row. A merged card can stand for copies in several KPI widgets
  // (`refs`); every copy is removed, or a hidden duplicate would take its place. Within one widget the
  // highest index goes first, so removing a card never shifts the position of one still to remove.
  // The server also drops each card's query, so a refresh can't bring it back.
  async function removeKpiCard(card) {
    if (!window.confirm(`Remove the "${card.label}" card from this dashboard?`)) return;
    setKpiError(null);
    const refs = [...(card.refs || [])].sort((a, b) =>
      a.widgetId === b.widgetId ? b.index - a.index : a.widgetId < b.widgetId ? -1 : 1);
    try {
      let widgets = null;
      for (const r of refs) widgets = (await api.deleteKpiCard(id, r.widgetId, r.index)).widgets;
      if (widgets) setDashboard((d) => ({ ...d, widgets }));
    } catch (e) {
      setKpiError(e.message);
    }
  }

  // Drag a Key metrics card to a new place. Optimistic like the widget reorder — only an order is
  // saved — and rolled back if the server refuses it.
  async function moveKpiCard(cards, from, to) {
    const keys = cards.map(cardKey);
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    const prev = dashboard.kpiOrder || [];
    setDashboard((d) => ({ ...d, kpiOrder: keys }));
    try {
      await api.setKpiOrder(id, keys);
    } catch (e) {
      setDashboard((d) => ({ ...d, kpiOrder: prev }));
      setKpiError(e.message);
    }
  }

  // The whole Key metrics row, i.e. every KPI widget on the dashboard.
  async function removeAllKpis(kpiWidgets) {
    if (!window.confirm("Remove all key metric cards from this dashboard?")) return;
    setKpiError(null);
    try {
      let widgets = null;
      for (const w of kpiWidgets) widgets = (await api.deleteDashboardWidget(id, w.id)).widgets;
      if (widgets) setDashboard((d) => ({ ...d, widgets }));
    } catch (e) {
      setKpiError(e.message);
    }
  }

  async function removeWidget(w) {
    const label = w.title || w.spec?.title || w.sourceTitle || "this widget";
    if (!window.confirm(`Remove "${label}" from this dashboard?`)) return;
    setRemovingId(w.id);
    setError(null);
    try {
      const res = await api.deleteDashboardWidget(id, w.id);
      setDashboard((d) => ({ ...d, widgets: res.widgets }));
      // Closing an expanded card that no longer exists avoids showing a deleted widget full-screen.
      setExpanded((e) => (e && e.id === w.id ? null : e));
    } catch (e) {
      setError(e.message);
    } finally {
      setRemovingId(null);
    }
  }

  // Optimistic, like ChatTitle's rename: the header updates immediately and rolls back only if the
  // server rejects it, so typing a new title doesn't visibly wait on a round-trip.
  async function renameDashboardTitle(title) {
    const prev = dashboard.title;
    setDashboard((d) => ({ ...d, title }));
    try {
      await api.renameDashboard(id, title);
    } catch (e) {
      setDashboard((d) => ({ ...d, title: prev }));
      setError(e.message);
    }
  }

  async function renameWidget(widgetId, title) {
    const prevWidgets = dashboard.widgets;
    setDashboard((d) => ({
      ...d,
      widgets: d.widgets.map((w) => (w.id === widgetId ? { ...w, title: title || undefined } : w)),
    }));
    try {
      await api.renameDashboardWidget(id, widgetId, title);
    } catch (e) {
      setDashboard((d) => ({ ...d, widgets: prevWidgets }));
      setError(e.message);
    }
  }

  /** Move `sourceId` to where `targetId` currently sits, then persist that order.
   *
   * Optimistic, like the two renames above: a reorder recomputes nothing and re-queries nothing, so
   * the only thing that can fail is the write itself — and making the card sit still until a
   * round-trip finishes would make dragging feel broken. Rolls back to the previous order if the
   * server rejects it.
   *
   * The move is computed over the RENDERED grid and then slotted back into the stored list, which is
   * exactly what the server does with the ids it is sent (see dashboards.reorder_dashboard_widgets).
   * Mirroring it matters only for a dashboard carrying a widget the grid never shows — an old
   * curator-built one's summary entry — but reordering the stored list directly instead would leave
   * the optimistic array and the saved array holding that entry at different indices, so a reload
   * would quietly produce a different list than the one on screen. */
  async function moveWidget(sourceId, targetId) {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const prevWidgets = dashboard.widgets;

    const offGrid = (w) => w.kind === "summary" || w.kind === "kpi";
    const grid = prevWidgets.filter((w) => !offGrid(w));
    const from = grid.findIndex((w) => w.id === sourceId);
    const to = grid.findIndex((w) => w.id === targetId);
    if (from === -1 || to === -1) return;
    grid.splice(to, 0, grid.splice(from, 1)[0]);

    // Drop the reordered cards back into the slots they collectively occupied; anything else (the
    // summary) keeps the index it already had.
    const queue = [...grid];
    const next = prevWidgets.map((w) => (offGrid(w) ? w : queue.shift()));

    setDashboard((d) => ({ ...d, widgets: next }));
    setSavingOrder(true);
    setError(null);
    try {
      await api.reorderDashboardWidgets(
        id,
        next.filter((w) => !offGrid(w)).map((w) => w.id)
      );
    } catch (e) {
      setDashboard((d) => ({ ...d, widgets: prevWidgets }));
      setError(e.message);
    } finally {
      setSavingOrder(false);
    }
  }

  async function addFromChats(chatIds) {
    setError(null);
    try {
      const res = await api.addDashboardWidgets(id, chatIds);
      setDashboard((d) => ({ ...d, widgets: res.widgets, chatIds: res.chatIds }));
      setAdding(false);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setDashboard(null);
    setError(null);
    api
      .getDashboard(id)
      .then((d) => !cancelled && setDashboard(d))
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Re-queries the dataset for every widget. Kept as an explicit action rather than polling: these
  // are full table scans, and a dashboard the user is reading shouldn't shift under them unasked.
  const onRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const fresh = await api.refreshDashboard(id, { dateFrom, dateTo });
      setDashboard(fresh);
      setRefreshInfo({ ...fresh.refresh, at: new Date() });
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const onClearRange = async () => {
    setDateFrom("");
    setDateTo("");
    setRefreshing(true);
    try {
      const fresh = await api.refreshDashboard(id, {});
      setDashboard(fresh);
      setRefreshInfo({ ...fresh.refresh, at: new Date() });
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  // All three are built by the server and follow the date range on screen. Excel keeps numbers as
  // numbers; the PDF and PowerPoint are reports — KPI cards, every chart and table, and their narration.
  // The PDF's charts are drawn here with the page's own Chart.js config, so they match the screen; the
  // PowerPoint makes native, editable charts from the data instead.
  const onDownload = async (format) => {
    setDownloadOpen(false);
    setDownloading(format);
    setDownloadError(null);
    const range = refreshInfo?.filtered ? { dateFrom, dateTo } : {};
    try {
      if (format === "xlsx") await api.downloadDashboardXlsx(id, range);
      else {
        const images = format === "pdf" ? dashboardChartImages(dashboard.widgets) : {};
        await api.downloadDashboardReport(id, format, { ...range, images });
      }
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this dashboard? This cannot be undone.")) return;
    await api.deleteDashboard(id);
    removeDashboard(id);
    navigate("/dashboards");
  };

  if (error) {
    return (
      <main className="main">
        <div className="page-body">
          <Alert variant="danger">{error}</Alert>
        </div>
      </main>
    );
  }
  if (!dashboard) {
    return (
      <main className="main">
        <div className="page-body">
          <Spinner animation="border" size="sm" />
        </div>
      </main>
    );
  }

  // Keyed by widget id so a card can show WHY it specifically didn't refresh, not just that something
  // in the dashboard didn't (see dashboards.refresh_dashboard's `notRefreshed`).
  const notRefreshedById = new Map(
    (refreshInfo?.notRefreshed || []).map((n) => [n.id, n])
  );

  // A dashboard shared with one of the viewer's roles is read-only for them: the server refuses every
  // edit, so the controls for one are not offered (canEdit comes from app/dashboards._viewable).
  const canEdit = dashboard.canEdit !== false;
  const sharedRoles = dashboard.sharedRoles || [];

  const widgets = dashboard.widgets || [];
  // The Dashboard Curator's executive summary (see app/dashboards.py build_dashboard_stream) covers
  // every candidate chat, not just the ones that earned a full card — it reads as an overview, not one
  // more widget, so it renders full-width above the grid rather than inside it.
  //const summaryWidget = widgets.find((w) => w.kind === "summary");
  const gridWidgets = widgets.filter((w) => w.kind !== "summary");
  // Every KPI widget renders as ONE Key metrics row above the grid (see mergeKpiWidgets): each chat that
  // asked for KPIs made its own widget, but on a dashboard they are one set of headline numbers.
  const kpiWidgets = gridWidgets.filter((w) => w.kind === "kpi");
  const tileWidgets = gridWidgets.filter((w) => w.kind !== "kpi");
  const keyMetrics = kpiWidgets.length ? mergeKpiWidgets(kpiWidgets, dashboard.kpiOrder || []) : null;
  const kpiStale = kpiWidgets.map((w) => notRefreshedById.get(w.id)).filter(Boolean);
  const kpiReasons = [...new Set(kpiStale.map((s) => s.reason).filter((r) => r && !HIDDEN_REFRESH_REASONS.has(r)))];
  const charts = widgets.filter((w) => w.kind === "chart").length;
  const tables = widgets.filter((w) => w.kind === "table").length;
  // Counted over gridWidgets, not widgets: an executive-summary widget is stored alongside the cards
  // but is not one, so counting it made the tile read "3 widgets" above a grid showing 2.
  const widgetCount = gridWidgets.length;
  const sourceTitles = [...new Set(gridWidgets.map((w) => w.sourceTitle || "Untitled chat"))];
  // Rows across every table widget — the volume of data this dashboard is actually built on.
  const totalRows = widgets
    .filter((w) => w.kind === "table")
    .reduce((n, w) => n + (w.spec?.matched_rows ?? w.spec?.rows?.length ?? 0), 0);

  return (
    <main className="main">
      <div className="dash-topbar">
        <div className="dash-topbar-left">
          <DashboardTitle title={dashboard.title} onRename={canEdit ? renameDashboardTitle : null} />
          <p>
            {sharedRoles.length > 0 && (
              <span className="dash-shared-badge" title={canEdit ? `Shared with: ${sharedRoles.join(", ")}` : `Shared by ${dashboard.username}`}>
                G
              </span>
            )}
            SAVED {new Date(dashboard.createdAt * 1000).toLocaleString().toUpperCase()} ·{" "}
            {/* widgetCount, not widgets.length: the executive-summary entry is stored alongside the
                cards but is not one, so counting it here said "4 WIDGETS" above a grid of 3 — and
                disagreed with the WIDGETS tile below, which already excludes it. */}
            {widgetCount} WIDGET{widgetCount === 1 ? "" : "S"}
            {sharedRoles.length > 0 && (
              <span className="dash-share-note">
                {canEdit ? `· SHARED WITH ${sharedRoles.join(", ").toUpperCase()}` : `· SHARED BY ${(dashboard.username || "").toUpperCase()} · READ-ONLY`}
              </span>
            )}
          </p>
        </div>
        <div className="dash-actions">
          {/* A dashboard is a snapshot until it's refreshed; after that it reflects the dataset as of
              that moment, or a date-filtered view of it. The badge says which, so nobody reads stale
              numbers as live ones. */}
          <span className={"badge-snapshot" + (refreshInfo ? " live" : "")}>
            {refreshInfo ? (refreshInfo.filtered ? "● FILTERED VIEW" : "● REFRESHED") : "● SNAPSHOT"}
          </span>
          {canEdit && user?.isAdmin && (
            <button className="dash-btn" onClick={() => setSharing(true)}>
              ⇪ Share
            </button>
          )}
          <span className="dash-menu">
            <button className="dash-btn" onClick={() => setDownloadOpen((v) => !v)} disabled={downloading}>
              {downloading ? `Preparing ${downloading === "pptx" ? "PowerPoint" : downloading.toUpperCase()}…` : "⤓ Download ▾"}
            </button>
            {downloadOpen && (
              <div className="dash-menu-list" onMouseLeave={() => setDownloadOpen(false)}>
                <button onClick={() => onDownload("pdf")}>PDF report</button>
                <button onClick={() => onDownload("pptx")}>PowerPoint (.pptx)</button>
                <button onClick={() => onDownload("xlsx")}>Excel (.xlsx)</button>
              </div>
            )}
          </span>
          <button className="dash-btn" onClick={() => setShowLineage(true)}>
            ⋔ Lineage
          </button>
          {canEdit && (
            <button className="dash-btn danger" onClick={onDelete}>
              ✕ Delete
            </button>
          )}
        </div>
      </div>

      {downloadError && (
        <Alert variant="danger" dismissible onClose={() => setDownloadError(null)} className="dash-download-error">
          Download failed: {downloadError}
        </Alert>
      )}

      <div className="dash-filters">
        {/* Not "Booking date": each widget is filtered on its OWN dataset's date column, chosen
            server-side (see tools/dataset.default_date_field). Naming one column here promised a
            single field that does not exist across a dashboard built from several tables. */}
        <span className="filter-label" title="Each widget is filtered on its dataset's own date column">
          Date range
        </span>
        <input
          type="date"
          className="filter-input"
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(e) => setDateFrom(e.target.value)}
          aria-label="From date"
        />
        <span className="filter-sep">→</span>
        <input
          type="date"
          className="filter-input"
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(e) => setDateTo(e.target.value)}
          aria-label="To date"
        />
        {(dateFrom || dateTo) && (
          <button className="dash-btn subtle" onClick={onClearRange} disabled={refreshing}>
            Clear
          </button>
        )}
        <button className="dash-btn primary" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "⟳ Refresh"}
        </button>
        {canEdit && (
          <button className="dash-btn subtle" onClick={() => setBuilder({})}>
            ＋ Create widget
          </button>
        )}
        {canEdit && (
          <button className="dash-btn subtle" onClick={() => setAdding(true)}>
            + Add from a chat
          </button>
        )}
        {savingOrder && <span className="filter-note">Saving layout…</span>}
        {refreshInfo && (
          // The counts alone read as a fault report — "2 failed" for widgets that are perfectly fine
          // and simply cannot be narrowed to a date range. The server now says WHY each one stalled
          // (see dashboards._refresh_widget), so show that instead of leaving the number unexplained.
          <span className="filter-note">
            {refreshInfo.refreshed} of {refreshInfo.refreshed + refreshInfo.unchanged + refreshInfo.error} widget
            {refreshInfo.refreshed + refreshInfo.unchanged + refreshInfo.error === 1 ? "" : "s"} re-queried
            {refreshInfo.unchanged > 0 && ` · ${refreshInfo.unchanged} can't refresh`}
            {refreshInfo.error > 0 && ` · ${refreshInfo.error} failed`}
            {" · "}
            {refreshInfo.at.toLocaleTimeString()}
            {(refreshInfo.reasons || []).filter((r) => !HIDDEN_REFRESH_REASONS.has(r)).length > 0 && (
              <span className="refresh-reasons">
                {refreshInfo.reasons
                  .filter((r) => !HIDDEN_REFRESH_REASONS.has(r))
                  .map((r) => (
                    <span key={r}>Not re-queried because {r}.</span>
                  ))}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="page-body">
        <div className="kpi-row">
          <Kpi label="WIDGETS" value={widgetCount} hint="in this dashboard" icon="▦" />
          <Kpi
            label="KPI CARDS"
            value={keyMetrics ? keyMetrics.cards.length : 0}
            hint="headline figures"
            icon="◫"
          />
          <Kpi label="CHARTS" value={charts} hint={`${tables} table${tables === 1 ? "" : "s"}`} icon="▥" />
          <Kpi label="SOURCE CHATS" value={sourceTitles.length} hint="conversations combined" icon="▤" />
          <Kpi
            label="ROWS COVERED"
            value={totalRows.toLocaleString()}
            hint="across table widgets"
            icon="≡"
          />
        </div>

        {/* {summaryWidget && (
          <div className="summary-card">
            <div className="summary-card-label">✦ Executive Summary</div>
            <div
              className="summary-card-body"
              dangerouslySetInnerHTML={{ __html: mdToHtml(summaryWidget.spec.text) }}
            />
          </div>
        )} */}

        {keyMetrics && (
          // Bare cards on the page, like a dashboard's header row — no tab frame around them. The period
          // sits in the heading; narration is one click away instead of a tab.
          <section className="key-metrics">
            <div className="key-metrics-head">
              <span className="key-metrics-title">Key metrics</span>
              {keyMetrics.period && <span className="key-metrics-period">{fmtPeriod(keyMetrics.period)}</span>}
              {kpiStale.length > 0 && (
                <span
                  className={"pill " + (kpiStale.some((s) => s.status === "error") ? "danger" : "warning")}
                  title={kpiReasons.length ? `Not re-queried: ${kpiReasons.join("; ")}` : "Not re-queried"}
                >
                  {kpiStale.some((s) => s.status === "error") ? "⚠ failed" : "⚠ not refreshed"}
                </span>
              )}
              <span className="key-metrics-actions">
                {keyMetrics.narration && (
                  <button
                    className={"key-metrics-link" + (showKpiNarration ? " on" : "")}
                    onClick={() => setShowKpiNarration((v) => !v)}
                  >
                    Narration
                  </button>
                )}
                {canEdit && (
                  <button
                    className="dt-icon-btn danger"
                    title="Remove all key metrics from dashboard"
                    onClick={() => removeAllKpis(kpiWidgets)}
                  >
                    ✕
                  </button>
                )}
              </span>
            </div>
            {kpiReasons.length > 0 && <div className="dashboard-widget-reason">{kpiReasons.join(". ")}.</div>}
            {kpiError && (
              <Alert variant="danger" dismissible onClose={() => setKpiError(null)} className="key-metrics-error">
                {kpiError}
              </Alert>
            )}
            <KpiWidget
              spec={{ ...keyMetrics, period: null }}
              onRemoveCard={canEdit ? (_, card) => removeKpiCard(card) : undefined}
              onMoveCard={canEdit ? (from, to) => moveKpiCard(keyMetrics.cards, from, to) : undefined}
            />
            {showKpiNarration && keyMetrics.narration && (
              <div className="key-metrics-narration" dangerouslySetInnerHTML={{ __html: mdToHtml(keyMetrics.narration) }} />
            )}
          </section>
        )}

        <div className="widget-grid">
          {tileWidgets.map((w) => {
            const stale = notRefreshedById.get(w.id);
            return (
            <div
              key={w.id}
              className={
                "dashboard-widget" +
                (dragId === w.id ? " is-dragging" : "") +
                (overId === w.id && dragId !== w.id ? " is-drop-target" : "")
              }
              draggable={armedId === w.id}
              onDragStart={(e) => {
                setDragId(w.id);
                e.dataTransfer.effectAllowed = "move";
                // Firefox ignores a dragstart that sets no data at all.
                e.dataTransfer.setData("text/plain", w.id);
              }}
              onDragEnd={() => {
                setArmedId(null);
                setDragId(null);
                setOverId(null);
              }}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();          // without this the drop event never fires
                e.dataTransfer.dropEffect = "move";
                if (overId !== w.id) setOverId(w.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                moveWidget(dragId, w.id);
                setOverId(null);
              }}
            >
              {/* Source on the left, controls on the right. A grid cell is narrow enough that a chart
                  is often unreadable in place, so every card can be opened full-size; and every card
                  can be dropped from the dashboard or dragged to a different position. */}
              <div className="dashboard-widget-head">
                {canEdit && (
                  <span
                    className="dashboard-widget-grip"
                    title="Drag to rearrange"
                    aria-label="Drag to rearrange"
                    // Arming happens here rather than on the card so only the grip starts a drag.
                    onMouseDown={() => setArmedId(w.id)}
                    onMouseUp={() => setArmedId(null)}
                  >
                    ⠿
                  </span>
                )}
                <WidgetTitle widget={w} onRename={canEdit ? (title) => renameWidget(w.id, title) : null} />
                {stale && (
                  // Always visible (unlike the hover-only action icons below) — it explains a state of
                  // the card, not an action on it, so it shouldn't disappear until the user hovers.
                  <span
                    className={"pill " + (stale.status === "error" ? "danger" : "warning")}
                    title={
                      stale.reason && !HIDDEN_REFRESH_REASONS.has(stale.reason)
                        ? `Not re-queried: ${stale.reason}`
                        : "Not re-queried"
                    }
                  >
                    {stale.status === "error" ? "⚠ failed" : "⚠ not refreshed"}
                  </span>
                )}
                <span className="dashboard-widget-actions">
                  {canEdit && w.spec?._source?.tool === "custom_query" && (
                    <button className="dt-icon-btn" title="Edit this widget's query" onClick={() => setBuilder({ widget: w })}>
                      ✎
                    </button>
                  )}
                  <button
                    className="dt-icon-btn"
                    title="Expand"
                    onClick={() => setExpanded(w)}
                  >
                    ⤢
                  </button>
                  {canEdit && (
                    <button
                      className="dt-icon-btn danger"
                      title="Remove from dashboard"
                      disabled={removingId === w.id}
                      onClick={() => removeWidget(w)}
                    >
                      ✕
                    </button>
                  )}
                </span>
              </div>
              {stale?.reason && !HIDDEN_REFRESH_REASONS.has(stale.reason) && (
                // The pill above says a card is stale; this says WHY, without making the user
                // hover to find out — the reason is the whole point when three widgets are all
                // marked "not refreshed" for three different reasons.
                <div className="dashboard-widget-reason">{stale.reason}.</div>
              )}
              <WidgetCard kind={w.kind} spec={w.spec} competitive />
            </div>
            );
          })}
        </div>
      </div>

      {expanded && (
        // Rendered from `gridWidgets` by id rather than from the captured object, so an expanded card
        // still reflects a refresh that lands while it is open.
        <div className="modal-backdrop" onClick={() => setExpanded(null)}>
          <div className="widget-modal" onClick={(e) => e.stopPropagation()}>
            <div className="form-modal-head">
              <div className="form-modal-title">
                {expanded.title || expanded.spec?.title || expanded.sourceTitle || "Widget"}
              </div>
              <button className="icon-btn" onClick={() => setExpanded(null)} title="Close">
                ✕
              </button>
            </div>
            <div className="widget-modal-body">
              <WidgetCard kind={expanded.kind} spec={expanded.spec} competitive />
            </div>
          </div>
        </div>
      )}

      {adding && (
        <AddChatsModal
          existingChatIds={dashboard.chatIds || []}
          onSubmit={addFromChats}
          onClose={() => setAdding(false)}
        />
      )}

      {builder && (
        <WidgetBuilderModal
          dashboardId={id}
          editing={builder.widget || null}
          dateRange={refreshInfo?.filtered ? { dateFrom, dateTo } : null}
          onClose={() => setBuilder(null)}
          onSaved={(widgets) => {
            setDashboard((d) => ({ ...d, widgets }));
            setBuilder(null);
          }}
        />
      )}

      {sharing && (
        <ShareDashboardModal
          dashboardId={id}
          onClose={() => setSharing(false)}
          onSaved={(roles) => {
            setDashboard((d) => ({ ...d, sharedRoles: roles }));
            setSharing(false);
            refreshDashboardList(); // the sidebar's G badge
          }}
        />
      )}

      {showLineage && <LineageModal dashboard={dashboard} onClose={() => setShowLineage(false)} />}
    </main>
  );
}
